import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {type AxiosResponse} from 'axios';
import {type ApplicationContextInterface} from "@@/package/application/applicationContext";
import type LearningCourseResponse from "~~/package/apis/learning/response/LearningCourseResponse";
import LearningApi from "~~/package/apis/learning/LearningApi";
import {LearningHelper} from "~~/package/mapper/learning/LearningHelper";
import {LearningCourseModel} from "~~/package/mapper/learning/model/LearningCourseModel";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import type TaskResponse from "~~/package/apis/learning/response/TaskResponse";
import {LangType} from "~~/package/mapper/learning/model/LangType";
import type TaskCompleteRequest from "~~/package/apis/learning/request/TaskCompleteRequest";
import type TaskAnswerSaveRequest from "~~/package/apis/learning/request/TaskAnswerSaveRequest";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import type TaskAnswerResponse from "~~/package/apis/learning/response/TaskAnswerResponse";
import type CustomResponse from "~~/package/types/ResponseType";
import {LearningTokenData} from "~~/package/mapper/learning/model/LearningTokenData";

export default class LearningMapper {

    protected learningApi: LearningApi;
    constructor(context: ApplicationContextInterface) {
        this.learningApi =  context.getService<LearningApi>(DependencyInjectId.LearningApi)
    }

    public async getLearningCourse(id: number, lang: string): Promise<LearningCourseModel> {
        const courseResponse: CustomResponse<LearningCourseResponse> = (await this.learningApi.getLearningCourse(id, lang)).data;
        return LearningHelper.mappingLearningCourse(courseResponse.data);
    }

    public async getTask(taskId: number, lang: string): Promise<TaskModel> {
        const taskResponse: CustomResponse<TaskResponse> = (await this.learningApi.getTask(taskId, lang)).data;
        return LearningHelper.mappingTask(taskResponse.data);
    }

    public async getToken(courseId: number, taskId: number, langType: LangType, locale: string, domain?: string, domainId?: number): Promise<LearningTokenData> {
        const tokenResponse: AxiosResponse<CustomResponse<string>> = await this.learningApi.getToken(courseId, taskId, langType, locale, domain, domainId);
        return LearningTokenData.create({
            token: tokenResponse.data.data,
            hmac: tokenResponse.headers.hmac ?? "",
            issuedAt: tokenResponse.headers.issued_at ?? 0,
        });
    }

    public async requestToSubChapterComplete(request: TaskCompleteRequest, tokenData: LearningTokenData): Promise<TaskCompleteResponse> {
        const completeResponse: CustomResponse<TaskCompleteResponse> = (await this.learningApi.requestToSubChapterComplete(request, tokenData)).data;
        return completeResponse.data;
    }


    public async getTaskAnswer(taskId: number): Promise<TaskAnswerResponse> {
        const answerResponse: CustomResponse<TaskAnswerResponse> = (await this.learningApi.getTaskAnswer(taskId)).data;
        return answerResponse.data;
    }

    public async saveCodeHistory(taskId: number, code: string): Promise<boolean> {
        return (await this.learningApi.saveCodeHistory(taskId, code)).data.data;
    }

    public async saveCode(request: TaskAnswerSaveRequest): Promise<boolean> {
        return (await this.learningApi.saveCode(request)).data.data;
    }

    public async saveUserCode(taskId: number, domainType: string, domainId: number, code: string): Promise<boolean> {
        return (await this.learningApi.saveUserCode(taskId, domainType, domainId, code)).data.data;
    }

    public async getLatestSavedCode(taskId: number, domainType: string, domainId: number): Promise<string> {
        const answerResponse: CustomResponse<string> = (await this.learningApi.getLatestSavedCode(taskId, domainType, domainId)).data;
        return answerResponse.data;
    }

    public async saveTaskDuration(taskId: number, domainType: string, domainId: number, amountInSec: number): Promise<boolean> {
        return (await this.learningApi.saveTaskDuration(taskId, domainType, domainId, amountInSec)).data.data;
    }

    public async getTaskForValidate(uuid: string): Promise<TaskModel> {
        const taskResponse: CustomResponse<TaskResponse> = (await this.learningApi.getTaskForValidate(uuid)).data;
        return LearningHelper.mappingTask(taskResponse.data);
    }

    public async getTaskValidity(uuid: string, request: TaskCompleteRequest, tokenData: LearningTokenData): Promise<TaskCompleteResponse> {
        const completeResponse: CustomResponse<TaskCompleteResponse> = (await this.learningApi.getTaskValidity(uuid, request, tokenData)).data;
        return completeResponse.data;
    }
}
